import Button from '@cobliteam/ui/components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import type { ComponentProps, ForwardedRef, HTMLAttributes, KeyboardEventHandler, ReactNode } from 'react';
import { forwardRef, useEffect, useRef } from 'react';

export type PromptInputProps = HTMLAttributes<HTMLFormElement>;

export const PromptInput = ({ className, ...props }: PromptInputProps) => (
  <form className={cn('w-full overflow-hidden rounded-[12px] border border-gray-5 bg-white shadow-[0_8px_22px_rgba(15,23,42,0.06)]', className)} {...props} />
);

export type PromptInputTextareaProps = ComponentProps<typeof Textarea> & {
  minHeight?: number;
  maxHeight?: number;
};

export type PromptInputEditorProps = HTMLAttributes<HTMLDivElement> & {
  disabled?: boolean;
  minHeight?: number;
  maxHeight?: number;
};

const promptInputFieldClassName = cn(
  '!border-none !shadow-none !text-xs w-full rounded-none bg-white px-3.5 pt-1 pb-1.5 leading-5',
  'scrollbar-thin scrollbar-thumb-gray-6 scrollbar-track-gray-1',
  'focus:!border-none focus:!shadow-none focus-visible:!border-none focus-visible:!shadow-none outline-none ring-0 placeholder:text-gray-8 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'
);

const assignTextareaRef = (ref: ForwardedRef<HTMLTextAreaElement>, node: HTMLTextAreaElement | null) => {
  if (typeof ref === 'function') {
    ref(node);
    return;
  }

  if (ref) {
    (ref as { current: HTMLTextAreaElement | null }).current = node;
  }
};

export const PromptInputTextarea = forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(
  ({ onChange, onKeyDown, className, placeholder = 'O que você deseja saber?', minHeight = 52, maxHeight = 168, autoFocus, ...props }, ref) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;

      if (e.key === 'Enter') {
        if (e.shiftKey) {
          return;
        }

        if (e.nativeEvent.isComposing) {
          return;
        }

        e.preventDefault();
        const form = e.currentTarget.form;
        if (form) {
          form.requestSubmit();
        }
      }
    };

    useEffect(() => {
      if (innerRef.current && autoFocus !== false) {
        innerRef.current.focus();
      }
    }, [autoFocus]);

    return (
      <Textarea
        ref={(node) => {
          innerRef.current = node;
          assignTextareaRef(ref, node);
        }}
        autoFocus
        className={cn(promptInputFieldClassName, 'resize-none', className)}
        name='message'
        onChange={(e) => {
          onChange?.(e);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{ minHeight, maxHeight }}
        {...props}
      />
    );
  }
);
PromptInputTextarea.displayName = 'PromptInputTextarea';

export const PromptInputEditor = forwardRef<HTMLDivElement, PromptInputEditorProps>(({ className, disabled, minHeight = 52, maxHeight = 168, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'relative w-full overflow-y-auto',
      '[&_.ProseMirror]:min-h-full [&_.ProseMirror]:whitespace-pre-wrap [&_.ProseMirror]:break-words [&_.ProseMirror]:leading-7 [&_.ProseMirror]:outline-none',
      '[&_.ProseMirror]:focus:outline-none [&_.ProseMirror_>p]:my-0',
      '[&_.ProseMirror_.mention-chip]:mx-0.5 [&_.ProseMirror_.mention-chip]:my-0.5 [&_.ProseMirror_.mention-chip]:inline-flex [&_.ProseMirror_.mention-chip]:h-6 [&_.ProseMirror_.mention-chip]:max-w-[14rem] [&_.ProseMirror_.mention-chip]:items-center [&_.ProseMirror_.mention-chip]:gap-1 [&_.ProseMirror_.mention-chip]:border [&_.ProseMirror_.mention-chip]:px-1.5 [&_.ProseMirror_.mention-chip]:align-middle [&_.ProseMirror_.mention-chip]:font-medium [&_.ProseMirror_.mention-chip]:text-[11px] [&_.ProseMirror_.mention-chip]:leading-none',
      '[&_.ProseMirror_.mention-chip]:rounded-none [&_.ProseMirror_.mention-chip]:border-gray-4 [&_.ProseMirror_.mention-chip]:bg-gray-1 [&_.ProseMirror_.mention-chip]:text-gray-11',
      '[&_.ProseMirror_.mention-chip-icon]:flex [&_.ProseMirror_.mention-chip-icon]:size-3.5 [&_.ProseMirror_.mention-chip-icon]:shrink-0 [&_.ProseMirror_.mention-chip-icon]:items-center [&_.ProseMirror_.mention-chip-icon]:justify-center',
      '[&_.ProseMirror_.mention-chip-icon_svg]:size-3',
      '[&_.ProseMirror_.mention-chip-type]:shrink-0 [&_.ProseMirror_.mention-chip-type]:font-semibold [&_.ProseMirror_.mention-chip-type]:text-[9px] [&_.ProseMirror_.mention-chip-type]:uppercase [&_.ProseMirror_.mention-chip-type]:tracking-[0.05em] [&_.ProseMirror_.mention-chip-type]:opacity-80',
      '[&_.ProseMirror_.mention-chip-label]:min-w-0 [&_.ProseMirror_.mention-chip-label]:truncate [&_.ProseMirror_.mention-chip-label]:font-semibold',
      '[&_.ProseMirror_.mention-chip--vehicles]:border-primary-7/30 [&_.ProseMirror_.mention-chip--vehicles]:bg-primary-1 [&_.ProseMirror_.mention-chip--vehicles]:text-primary-11',
      '[&_.ProseMirror_.mention-chip--drivers]:border-warning-7/30 [&_.ProseMirror_.mention-chip--drivers]:bg-warning-1 [&_.ProseMirror_.mention-chip--drivers]:text-warning-11',
      '[&_.ProseMirror_.mention-chip--devices]:border-accessory-purple-7/30 [&_.ProseMirror_.mention-chip--devices]:bg-accessory-purple-1 [&_.ProseMirror_.mention-chip--devices]:text-accessory-purple-11',
      '[&_.ProseMirror_.mention-chip--vehicleGroups]:border-success-7/30 [&_.ProseMirror_.mention-chip--vehicleGroups]:bg-success-1 [&_.ProseMirror_.mention-chip--vehicleGroups]:text-success-11',
      '[&_.ProseMirror_.mention-chip--geofences]:border-accessory-sky-7/30 [&_.ProseMirror_.mention-chip--geofences]:bg-accessory-sky-1 [&_.ProseMirror_.mention-chip--geofences]:text-accessory-sky-11',
      disabled && 'cursor-not-allowed opacity-60',
      className
    )}
    aria-disabled={disabled}
    style={{ minHeight, maxHeight }}
    {...props}
  />
));
PromptInputEditor.displayName = 'PromptInputEditor';

export type PromptInputToolbarProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputToolbar = ({ className, ...props }: PromptInputToolbarProps) => <div className={cn('!p-1.5 flex items-center justify-end', className)} {...props} />;

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = ({ className, ...props }: PromptInputToolsProps) => <div className={cn('flex items-center gap-1', className)} {...props} />;

type PromptInputButtonVariant = 'ghost' | 'surface';

export type PromptInputButtonProps = Omit<ComponentProps<typeof Button>, 'size' | 'variant' | 'color'> & {
  variant?: PromptInputButtonVariant;
};

export const PromptInputButton = ({ variant = 'ghost', className, type = 'button', children, ...props }: PromptInputButtonProps) => (
  <Button type={type} size='sm' color='default' variant={variant} className={cn('rounded-[6px]', className)} {...props}>
    {children}
  </Button>
);

export type PromptInputSubmitProps = Omit<ComponentProps<typeof Button>, 'size' | 'variant' | 'color' | 'icon' | 'children'> & {
  status?: 'submitted' | 'streaming' | 'error' | 'ready';
  variant?: 'contained' | 'ghost' | 'solid';
  children?: ReactNode;
  size?: 'sm' | 'md';
};

export const PromptInputSubmit = ({ className, variant = 'contained', status, size = 'sm', type = 'submit', children, ...props }: PromptInputSubmitProps) => {
  let IconComponent = <Icon name='send' size='sm' />;

  if (status === 'submitted') {
    IconComponent = <Icon name='square' size='sm' />;
  } else if (status === 'streaming') {
    IconComponent = <Icon name='square' size='sm' />;
  } else if (status === 'error') {
    IconComponent = <Icon name='x' size='sm' />;
  }

  const buttonSize = size === 'sm' ? 'sm' : 'md';

  const isSolid = variant === 'solid';
  const solidClass = isSolid && !props.disabled ? 'bg-gradient-to-r from-[#0745cf] to-[#9847ee] text-white border-0 hover:opacity-90' : undefined;
  const buttonColor = isSolid ? 'default' : variant === 'contained' ? 'primary' : 'default';

  if (children) {
    return (
      <Button type={type} size={buttonSize} color={buttonColor} variant='contained' className={cn('rounded-[6px]', solidClass, className)} {...props}>
        {children}
      </Button>
    );
  }

  return (
    <Button
      type={type}
      size={buttonSize}
      color={buttonColor}
      variant='contained'
      className={cn('h-8 w-8 shrink-0 rounded-[6px] p-0', solidClass, className)}
      data-testid='ai-prompt-submit'
      {...props}
    >
      {IconComponent}
    </Button>
  );
};

export type PromptInputModelSelectProps = ComponentProps<typeof Select>;

export const PromptInputModelSelect = (props: PromptInputModelSelectProps) => <Select {...props} />;

export type PromptInputModelSelectTriggerProps = ComponentProps<typeof SelectTrigger>;

export const PromptInputModelSelectTrigger = ({ className, ...props }: PromptInputModelSelectTriggerProps) => (
  <SelectTrigger
    className={cn(
      'border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors',
      'hover:bg-accent hover:text-foreground [&[aria-expanded="true"]]:bg-accent [&[aria-expanded="true"]]:text-foreground',
      className
    )}
    {...props}
  />
);

export type PromptInputModelSelectContentProps = ComponentProps<typeof SelectContent>;

export const PromptInputModelSelectContent = ({ className, ...props }: PromptInputModelSelectContentProps) => <SelectContent className={cn(className)} {...props} />;

export type PromptInputModelSelectItemProps = ComponentProps<typeof SelectItem>;

export const PromptInputModelSelectItem = ({ className, ...props }: PromptInputModelSelectItemProps) => <SelectItem className={cn(className)} {...props} />;

export type PromptInputModelSelectValueProps = ComponentProps<typeof SelectValue>;

export const PromptInputModelSelectValue = ({ className, ...props }: PromptInputModelSelectValueProps) => <SelectValue className={cn(className)} {...props} />;
